'use client';

import type { FocusEvent as ReactFocusEvent, PointerEvent as ReactPointerEvent, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { clearWindowTimeout, setWindowTimeout } from '@/lib/utils/window-timers';

type HighlightId = string | number;

interface InstantHoverHighlightConfig {
  fadeDurationMs?: number;
}

interface ItemHandlers<ElementType extends HTMLElement> {
  onFocusCapture?: (event: ReactFocusEvent<ElementType>) => void;
  onPointerEnter?: (event: ReactPointerEvent<ElementType>) => void;
}

interface ContainerHandlers<ElementType extends HTMLElement> {
  onBlurCapture?: (event: ReactFocusEvent<ElementType>) => void;
  onPointerLeave?: (event: ReactPointerEvent<ElementType>) => void;
}

interface HighlightState {
  isFadingOut: boolean;
  isHighlighted: boolean;
}

interface InstantHoverHighlightResult<ContainerElement extends HTMLElement, ItemElement extends HTMLElement> {
  containerProps: ContainerHandlers<ContainerElement> & {
    ref: (node: ContainerElement | null) => void;
  };
  getItemProps: <Id extends HighlightId>(id: Id, handlers?: ItemHandlers<ItemElement>) => ItemHandlers<ItemElement>;
  getItemState: <Id extends HighlightId>(id: Id) => HighlightState;
  highlightedId: HighlightId | null;
  isFadingOut: boolean;
}

const getNodeConstructor = (): typeof Node | null => {
  const browserWindow = getBrowserWindow();
  if (browserWindow?.Node) {
    return browserWindow.Node;
  }
  if (globalThis !== undefined && 'Node' in globalThis) {
    const globalNode = (globalThis as typeof globalThis & { Node?: typeof Node }).Node;
    if (globalNode) {
      return globalNode;
    }
  }
  return null;
};

const NodeLikeSchema = z.object({
  nodeType: z.number(),
});

function isNodeLike(value: unknown): value is Node {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const NodeCtor = getNodeConstructor();
  if (NodeCtor && value instanceof NodeCtor) {
    return true;
  }
  const parsed = NodeLikeSchema.safeParse(value);
  return parsed.success;
}

const isDocumentNode = (node: Node): node is Document => 'documentElement' in node;

function resolveOwnerDocument(node: Node): Document | null {
  if (isDocumentNode(node)) {
    return node;
  }
  return node.ownerDocument ?? null;
}

function containerContains<ContainerElement extends HTMLElement>(
  container: ContainerElement | null,
  maybeNode: unknown
): boolean {
  if (!(container && isNodeLike(maybeNode))) {
    return false;
  }
  const containerDocument = container.ownerDocument ?? resolveOwnerDocument(container);
  const targetDocument = resolveOwnerDocument(maybeNode);
  if (containerDocument && targetDocument && containerDocument !== targetDocument) {
    return false;
  }
  try {
    return container.contains(maybeNode);
  } catch {
    return false;
  }
}

function composeEventHandlers<Event extends SyntheticEvent>(
  original?: (event: Event) => void,
  ours?: (event: Event) => void
) {
  return (event: Event) => {
    original?.(event);
    if (!event.defaultPrevented) {
      ours?.(event);
    }
  };
}

/**
 * Provides instant hover feedback for list/table rows while allowing a short fade when the pointer exits the container.
 * Based on Emil Kowalski’s “You don’t need animations” guidance: https://emilkowal.ski/ui/you-dont-need-animations
 */
export function useInstantHoverHighlight<
  ContainerElement extends HTMLElement = HTMLElement,
  ItemElement extends HTMLElement = HTMLElement,
>(config: InstantHoverHighlightConfig = {}): InstantHoverHighlightResult<ContainerElement, ItemElement> {
  const { fadeDurationMs = 180 } = config;

  const containerReference = useRef<ContainerElement | null>(null);
  const fadeTimeoutReference = useRef<number | null>(null);

  const [highlightedId, setHighlightedId] = useState<HighlightId | null>(null);
  const [lastHighlightedId, setLastHighlightedId] = useState<HighlightId | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const clearFadeTimeout = useCallback(() => {
    if (fadeTimeoutReference.current !== null) {
      clearWindowTimeout(fadeTimeoutReference.current);
      fadeTimeoutReference.current = null;
    }
  }, []);

  const handlePointerEnter = useCallback(
    (id: HighlightId) => {
      clearFadeTimeout();
      setHighlightedId(id);
      setLastHighlightedId(id);
      setIsFadingOut(false);
    },
    [clearFadeTimeout]
  );

  const scheduleFadeOut = useCallback(() => {
    clearFadeTimeout();
    setIsFadingOut(true);
    const timeoutId = setWindowTimeout(() => {
      setIsFadingOut(false);
      setLastHighlightedId(null);
      fadeTimeoutReference.current = null;
    }, fadeDurationMs);
    if (timeoutId === null) {
      setIsFadingOut(false);
      setLastHighlightedId(null);
      fadeTimeoutReference.current = null;
      return;
    }
    fadeTimeoutReference.current = timeoutId;
  }, [clearFadeTimeout, fadeDurationMs]);

  const handleContainerPointerLeave = useCallback(
    (event: ReactPointerEvent<ContainerElement>) => {
      const nextTarget = event.relatedTarget;
      if (containerContains(containerReference.current, nextTarget)) {
        return;
      }
      if (highlightedId === null) {
        return;
      }
      setHighlightedId(null);
      scheduleFadeOut();
    },
    [highlightedId, scheduleFadeOut]
  );

  const handleContainerBlur = useCallback(
    (event: ReactFocusEvent<ContainerElement>) => {
      const nextTarget = event.relatedTarget;
      if (containerContains(containerReference.current, nextTarget)) {
        return;
      }
      if (highlightedId === null) {
        return;
      }
      setHighlightedId(null);
      scheduleFadeOut();
    },
    [highlightedId, scheduleFadeOut]
  );

  useEffect(
    () => () => {
      clearFadeTimeout();
    },
    [clearFadeTimeout]
  );

  const setContainerReference = useCallback((node: ContainerElement | null) => {
    containerReference.current = node;
  }, []);

  const getItemState = useCallback(
    (id: HighlightId): HighlightState => ({
      isHighlighted: highlightedId === id,
      isFadingOut: isFadingOut && lastHighlightedId === id,
    }),
    [highlightedId, isFadingOut, lastHighlightedId]
  );

  const getItemProperties = useCallback(
    <Id extends HighlightId>(id: Id, handlers: ItemHandlers<ItemElement> = {}) => ({
      onPointerEnter: composeEventHandlers(handlers.onPointerEnter, (event: ReactPointerEvent<ItemElement>) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
          handlePointerEnter(id);
        }
      }),
      onFocusCapture: composeEventHandlers(handlers.onFocusCapture, () => {
        handlePointerEnter(id);
      }),
    }),
    [handlePointerEnter]
  );

  const containerHandlers = useMemo<InstantHoverHighlightResult<ContainerElement, ItemElement>['containerProps']>(
    () => ({
      ref: setContainerReference,
      onPointerLeave: handleContainerPointerLeave,
      onBlurCapture: handleContainerBlur,
    }),
    [handleContainerBlur, handleContainerPointerLeave, setContainerReference]
  );

  return {
    containerProps: containerHandlers,
    getItemProps: getItemProperties,
    getItemState,
    highlightedId,
    isFadingOut,
  };
}

export type { ItemHandlers as InstantHoverItemHandlers, HighlightState as InstantHoverHighlightState };
